import { apiClient } from '../config/ApiClient';
import type { ItemDTO } from '../dto/item/ItemDTO';
import { CreateItemDTO } from '../dto/item/CreateItemDTO';
import { ItemFeedResponse } from '../dto/item/ItemFeedResponse';

export type PublicItemsV2Params = {
  gender: string; // required
  category?: string;
  cursor?: string; // mutually exclusive with page
  colors?: string[];
  sizes?: string[];
  page?: number; // 1-based
};

export const createItem = (
  data: CreateItemDTO,
  thumbnail: File,
  images?: File[]
) => {
  const form = new FormData();
  form.append(
    'itemDto',
    new Blob([JSON.stringify(data)], { type: 'application/json' })
  );
  form.append('thumbnail', thumbnail);
  images?.forEach((img) => form.append('images', img));

  return apiClient.post<ItemDTO>('/item', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const fetchAllItemsPublic = () =>
  apiClient.get<ItemDTO[]>('/item/public');

export const fetchAllItemsAdmin = () => apiClient.get<ItemDTO[]>('/item');

export const searchItems = (keyword: string) =>
  apiClient.get<ItemDTO[]>('/item/public/search', { params: { keyword } });

export const fetchItemById = (id: string) =>
  apiClient.get<ItemDTO>(`/item/public/${id}`);

/**
 * Backend expects:
 * - itemDto (JSON, must include id)
 * - imageUrls?: string[] (existing images to keep)
 * - images?: File[] (new images)
 * - thumbnail?: File
 */
export const updateItem = (
  item: ItemDTO,
  thumbnail: File | null,
  images?: File[] | null,
  imageUrls?: string[] | null
) => {
  const form = new FormData();
  form.append(
    'itemDto',
    new Blob([JSON.stringify(item)], { type: 'application/json' })
  );

  imageUrls?.forEach((url) => form.append('imageUrls', url));
  images?.forEach((img) => form.append('images', img));
  if (thumbnail) form.append('thumbnail', thumbnail);

  return apiClient.put<ItemDTO>('/item', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const deleteItem = (itemId: string) =>
  apiClient.delete<void>(`/item/${itemId}`);

/**
 * GET /item/public/v2
 * Supports either cursor-based pagination (cursor) OR page-based (page).
 * Arrays (colors/sizes) are sent as repeated query params: colors=red&colors=blue
 */
export const fetchPublicItemsV2 = (params: PublicItemsV2Params) => {
  const sp = new URLSearchParams();

  // required
  sp.set('gender', params.gender);

  // optional scalars
  if (params.category?.trim()) sp.set('category', params.category.trim());
  if (params.cursor?.trim()) sp.set('cursor', params.cursor.trim());
  if (typeof params.page === 'number') sp.set('page', String(params.page));

  // arrays as repeated params
  params.colors?.forEach((c) => {
    const v = c?.trim();
    if (v) sp.append('colors', v);
  });
  params.sizes?.forEach((s) => {
    const v = s?.trim();
    if (v) sp.append('sizes', v);
  });

  return apiClient.get<ItemFeedResponse>('/item/public/v2', { params: sp });
};
